import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import Button from "../../../components/ui/Button"
import Card, { CardBody, CardHeader } from "../../../components/ui/Card"
import { usageApi } from "../../../services/api"

const formatInt = (n) => new Intl.NumberFormat("en-NG").format(Number(n || 0))
const TOPICS_PER_GENERATION = 10
const GENERATION_TYPE_LABELS = {
  DOCUMENTATION: "Documentation",
  PROPOSAL: "Proposal",
  SIWES_REPORT: "SIWES Report"
}

const ProgressBar = ({ value, max, remaining, color = "emerald", size = "md", showRemaining = false }) => {
  const safeMax = Number(max || 0)
  const safeValue = Number(value || 0)
  const safeRemaining = Number(remaining ?? 0)
  const pct = safeMax > 0 ? Math.max(0, Math.min(100, (safeValue / safeMax) * 100)) : 0

  const isAmber = color === "amber"
  const colorTrack = isAmber ? "bg-amber-50" : "bg-emerald-50"
  const colorFill = isAmber ? "from-amber-500/90 to-amber-600/90" : "from-emerald-500/90 to-emerald-600/90"

  const barHeight = size === "sm" ? "h-2.5" : "h-3"
  const marginTop = size === "sm" ? "mt-1.5" : "mt-2"

  return (
    <div className="w-full">
      <div className={`${barHeight} rounded-xl ${colorTrack} border border-slate-200/70 overflow-hidden`}>
        <div
          className={`h-full bg-gradient-to-r ${colorFill} transition-[width] duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className={`${marginTop} flex items-center justify-between text-xs ${size === "sm" ? "text-slate-600" : "text-slate-600"}`}>
        <span className="font-semibold text-slate-700">{formatInt(safeValue)} used</span>
        <span className="font-semibold">
          {showRemaining ? (
            <>
              <span className={isAmber ? "text-amber-800" : "text-emerald-800"}>Remaining {formatInt(safeRemaining)}</span>
            </>
          ) : (
            safeMax > 0 ? formatInt(safeMax) : "—"
          )}
        </span>
      </div>
    </div>
  )
}

const Usage = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [usage, setUsage] = useState(null)

  const idea = usage?.ideaGeneration || {}
  const docs = usage?.documentation?.items || []
  const totals = usage?.documentation?.totals || {}
  const docsByType = usage?.documentation?.byGenerationType || {}

  const ideaRemaining = Number(idea?.remaining ?? 0)
  const ideaAllowed = Number(idea?.allowed ?? 0)
  const ideaPackSize = Number(idea?.packSize ?? 0)
  const totalTopicsPerPack = ideaPackSize * TOPICS_PER_GENERATION

  const editRemainingTotal = Number(totals?.editsRemaining ?? 0)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError("")
    usageApi
      .get()
      .then((res) => {
        if (!active) return
        setUsage(res?.data?.data || null)
      })
      .catch((e) => {
        if (!active) return
        setError(e?.response?.data?.message || "Could not load usage")
        setUsage(null)
      })
      .finally(() => {
        if (!active) return
        setLoading(false)
      })

    return () => {
      active = false
    }
  }, [])

  const ideaCta = useMemo(() => {
    if (ideaRemaining > 0) return null
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-amber-900">Idea limit reached</p>
            <p className="text-xs text-amber-800 mt-1">
              Buy the idea generation pack to unlock more ideas.
            </p>
          </div>
          <Button size="sm" onClick={() => navigate("/pricing?ideaPack=1")}>
            Get idea pack
          </Button>
        </div>
      </div>
    )
  }, [ideaRemaining, navigate])

  return (
    <section className="space-y-5">
      <Card className="shadow-soft overflow-hidden">
        <CardHeader
          title="Usage & Limits"
          subtitle="A clear snapshot of your idea generations and project documentation progress."
          className="bg-gradient-to-r from-emerald-500/10 via-white to-white border-b border-slate-200/70"
          action={
            <Button variant="outline" size="sm" onClick={() => navigate("/pricing")}>
              Upgrade
            </Button>
          }
        />
        <CardBody className="space-y-5">
          {error ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-rose-800">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white border border-rose-200">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-rose-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86l-8.22 14.25a1.5 1.5 0 0 0 1.3 2.25h16.46a1.5 1.5 0 0 0 1.3-2.25L13.71 3.86a1.5 1.5 0 0 0-2.42 0Z" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{error}</p>
                  <p className="text-xs text-rose-800/90 mt-1">Please try again in a moment.</p>
                </div>
              </div>
            </div>
          ) : null}

          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm animate-pulse">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-2">
                    <div className="h-4 w-40 rounded bg-slate-100" />
                    <div className="h-3 w-56 rounded bg-slate-100" />
                  </div>
                  <div className="h-9 w-24 rounded-full bg-slate-100" />
                </div>
                <div className="grid grid-cols-3 gap-3 mt-4">
                  <div className="h-20 rounded-xl bg-slate-50 border border-slate-200/70" />
                  <div className="h-20 rounded-xl bg-slate-50 border border-slate-200/70" />
                  <div className="h-20 rounded-xl bg-slate-50 border border-slate-200/70" />
                </div>
                <div className="mt-4 h-3 rounded-xl bg-slate-100" />
                <div className="mt-2 h-3 w-56 rounded bg-slate-100" />
              </div>

              <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm animate-pulse">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-2">
                    <div className="h-4 w-52 rounded bg-slate-100" />
                    <div className="h-3 w-60 rounded bg-slate-100" />
                  </div>
                  <div className="h-9 w-32 rounded-full bg-slate-100" />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div className="h-20 rounded-xl bg-slate-50 border border-slate-200/70" />
                  <div className="h-20 rounded-xl bg-slate-50 border border-slate-200/70" />
                </div>
                <div className="mt-4 h-4 w-36 rounded bg-slate-100" />
                <div className="mt-3 space-y-3">
                  <div className="h-24 rounded-2xl bg-slate-50 border border-slate-200/70" />
                  <div className="h-24 rounded-2xl bg-slate-50 border border-slate-200/70" />
                </div>
              </div>
            </div>
          ) : null}

          {!loading && !error && usage ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Idea generations */}
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 border border-emerald-200">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a9 9 0 11-18 0 9 9 0 0118 0Z" />
                        </svg>
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Idea generations</p>
                        <p className="text-xs text-slate-600 mt-1">
                          Free limit: {formatInt(idea?.freeLimit)} + {formatInt(idea?.packSize)} generations per pack
                        </p>
                        <p className="text-xs text-slate-600 mt-1">
                          Total topics per pack: {formatInt(ideaPackSize)} x {TOPICS_PER_GENERATION} = {formatInt(totalTopicsPerPack)} topics
                        </p>
                      </div>
                    </div>

                    <span
                      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${
                        ideaRemaining === 0 ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"
                      }`}
                    >
                      Remaining {formatInt(ideaRemaining)}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                    <div className="rounded-xl border border-slate-200/70 bg-slate-50 p-3">
                      <p className="text-xs text-slate-600">Used</p>
                      <p className="text-lg font-bold text-slate-900">{formatInt(idea?.used)}</p>
                    </div>
                    <div className="rounded-xl border border-slate-200/70 bg-slate-50 p-3">
                      <p className="text-xs text-slate-600">Allowed</p>
                      <p className="text-lg font-bold text-slate-900">{formatInt(ideaAllowed)}</p>
                    </div>
                    <div className="rounded-xl border border-slate-200/70 bg-slate-50 p-3">
                      <p className="text-xs text-slate-600">Paid packs</p>
                      <p className="text-lg font-bold text-slate-900">{formatInt(idea?.paidPacks)}</p>
                    </div>
                    <div className="rounded-xl border border-slate-200/70 bg-slate-50 p-3">
                      <p className="text-xs text-slate-600">Topics per pack</p>
                      <p className="text-lg font-bold text-slate-900">{formatInt(totalTopicsPerPack)}</p>
                    </div>
                  </div>

                  <div className="mt-4">
                    <ProgressBar
                      value={idea?.used}
                      max={ideaAllowed}
                      remaining={ideaRemaining}
                      showRemaining
                      color={ideaRemaining === 0 ? "amber" : "emerald"}
                    />
                  </div>

                  {ideaCta}
                </div>
              </div>

              {/* Documentation */}
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-50 border border-slate-200">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 8h10M9 12h10m-6 4h6M7 3h10a2 2 0 0 1 2 2v14l-4-2-4 2-4-2-4 2V5a2 2 0 0 1 2-2Z" />
                        </svg>
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Project documentation</p>
                        <p className="text-xs text-slate-600 mt-1">
                          Generated sets and your remaining regenerations/edits per set.
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex items-center rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
                      {formatInt(usage?.documentation?.totalDocSets)} sets
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="rounded-xl border border-slate-200/70 bg-slate-50 p-3">
                      <p className="text-xs text-slate-600">Regens remaining</p>
                      <p className="text-lg font-bold text-slate-900">{formatInt(totals?.regenerationsRemaining)}</p>
                    </div>
                    <div className="rounded-xl border border-slate-200/70 bg-slate-50 p-3">
                      <p className="text-xs text-slate-600">Edits remaining</p>
                      <p className="text-lg font-bold text-slate-900">{formatInt(editRemainingTotal)}</p>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {["DOCUMENTATION", "PROPOSAL", "SIWES_REPORT"].map((type) => {
                      const stats = docsByType?.[type] || {}
                      const count = Number(stats?.totalDocSets || 0)
                      return (
                        <div key={type} className="rounded-xl border border-slate-200/70 bg-white p-3">
                          <p className="text-xs text-slate-600">{GENERATION_TYPE_LABELS[type]}</p>
                          <p className="text-lg font-bold text-slate-900">{formatInt(count)}</p>
                          <p className="text-xs text-slate-500">generated set{count === 1 ? "" : "s"}</p>
                        </div>
                      )
                    })}
                  </div>

                  {docs.length === 0 ? (
                    <div className="mt-5 rounded-2xl border border-dashed border-slate-300/80 bg-slate-50 p-6">
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white border border-slate-200">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
                          </svg>
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">No documentation yet</p>
                          <p className="text-sm text-slate-600 mt-1">
                            Create a project first, then your limits will appear here as you generate sets.
                          </p>
                        </div>
                      </div>
                      <div className="mt-4">
                        <Button size="sm" onClick={() => navigate("/dashboard/project")}>
                          Go to projects
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-5 space-y-3">
                      {docs.map((d) => {
                        const regen = d?.regeneration || {}
                        const edits = d?.edits || {}

                        const regenRemaining = Number(regen?.remaining ?? 0)
                        const editsRemaining = Number(edits?.remaining ?? 0)

                        const docKey = d.docId || d._id || `${d.projectTitle || "doc"}-${String(d.projectId || "")}`
                        return (
                          <div
                            key={docKey}
                            className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-semibold text-slate-900 truncate">{d.projectTitle || "Untitled project"}</p>
                                {d.projectDescription ? (
                                  <p className="text-xs text-slate-600 mt-1 line-clamp-2">{d.projectDescription}</p>
                                ) : null}
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold bg-slate-50 border-slate-200 text-slate-700">
                                  {GENERATION_TYPE_LABELS[d.generationType || "DOCUMENTATION"] || "Documentation"}
                                </span>
                                <span
                                  className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${
                                    regenRemaining === 0
                                      ? "bg-amber-50 border-amber-200 text-amber-800"
                                      : "bg-emerald-50 border-emerald-200 text-emerald-800"
                                  }`}
                                >
                                  Regens {formatInt(regen?.used)}/{formatInt(regen?.allowed)}
                                </span>
                                <span
                                  className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${
                                    editsRemaining === 0
                                      ? "bg-amber-50 border-amber-200 text-amber-800"
                                      : "bg-emerald-50 border-emerald-200 text-emerald-800"
                                  }`}
                                >
                                  Edits {formatInt(edits?.used)}/{formatInt(edits?.allowed)}
                                </span>
                              </div>
                            </div>

                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs font-semibold text-slate-700">Regenerations</p>
                                <div className="mt-2">
                                  <ProgressBar
                                    size="sm"
                                    value={regen?.used}
                                    max={regen?.allowed}
                                    remaining={regenRemaining}
                                    showRemaining
                                    color={regenRemaining === 0 ? "amber" : "emerald"}
                                  />
                                </div>
                              </div>

                              <div>
                                <p className="text-xs font-semibold text-slate-700">Edits</p>
                                <div className="mt-2">
                                  <ProgressBar
                                    size="sm"
                                    value={edits?.used}
                                    max={edits?.allowed}
                                    remaining={editsRemaining}
                                    showRemaining
                                    color={editsRemaining === 0 ? "amber" : "emerald"}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {!loading && !error && !usage ? (
            <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm text-center text-slate-600">
              No usage data available.
            </div>
          ) : null}
        </CardBody>
      </Card>
    </section>
  )
}

export default Usage

